import { Fragment } from "react";
import { AronMessageBubble } from "./AronMessageBubble";
import { PROVIDER_META, ProviderMark, providerFor } from "./AronProviderMark";
import type { AronMessage } from "../types";

/** „Od tej wiadomości odpowiada X" — model rozmowy da się przełączyć w trakcie, więc
 *  bez tego znacznika czytelnik przypisywałby całą historię modelowi, na którym wątek
 *  akurat stoi. Pokazuje się WYŁĄCZNIE w miejscu zmiany: wątek na jednym modelu
 *  wygląda dokładnie jak dotąd. */
function ModelChangeMark({ model, label }: { model: string; label: string }) {
  const provider = providerFor(model);
  const meta = provider ? PROVIDER_META[provider] : null;
  return (
    <div className="flex items-center gap-2" role="separator">
      <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
      <span
        className="inline-flex items-center gap-1.5 border border-gray-200 px-2 py-0.5 text-[11px] text-gray-500 dark:border-gray-700 dark:text-gray-400"
        title={`Kolejne odpowiedzi w tym wątku wykonał model ${model}.`}
      >
        {provider && meta && (
          <span style={{ color: meta.color }}>
            <ProviderMark provider={provider} size={11} />
          </span>
        )}
        od tej wiadomości odpowiada:{" "}
        <b className="font-medium text-gray-700 dark:text-gray-200">{label || model}</b>
      </span>
      <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
    </div>
  );
}

export function AronMessageList({
  messages,
  conversationId,
  readOnly = false,
}: {
  messages: AronMessage[];
  conversationId: string | null;
  /** Podgląd CUDZEJ rozmowy (audyt): karty propozycji pokazujemy, ale bez
   * przycisków decyzji — serwer i tak przyjmie je tylko od właściciela wątku,
   * a klikalny „Zatwierdź" na cudzej propozycji to zaproszenie do pomyłki. */
  readOnly?: boolean;
}) {
  // Auto-przewijanie należy do właściciela paska przewijania (AronChatView):
  // tam widać, czy czytelnik stoi na dole, a tu tylko rysujemy listę.
  let ostatniModel = "";
  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-5 px-3 py-4 sm:px-4 sm:py-6">
      {messages.map((m) => {
        // Znacznik stawiamy tylko przy realnej ZMIANIE między dwiema znanymi turami.
        // Tura bez stempla (sprzed wprowadzenia pola) nie udaje zmiany modelu.
        const model = m.role === "assistant" ? m.model_id || "" : "";
        const zmiana = !!model && !!ostatniModel && model !== ostatniModel;
        if (model) ostatniModel = model;
        return (
          <Fragment key={m.id}>
            {zmiana && <ModelChangeMark model={model} label={m.model_label || ""} />}
            <AronMessageBubble
              message={m}
              conversationId={conversationId}
              readOnly={readOnly}
            />
          </Fragment>
        );
      })}
    </div>
  );
}
